import React from 'react';
import { Shield, Languages, BookOpen, MessageSquare, BookMarked, GraduationCap, Users, Sparkles } from 'lucide-react';
import { resolveSource } from '@/lib/sourceLabels';

const ICON_MAP = {
  'shield': Shield,
  'languages': Languages,
  'book-open': BookOpen,
  'message-square': MessageSquare,
  'book-marked': BookMarked,
  'graduation-cap': GraduationCap,
  'users': Users,
  'sparkles': Sparkles,
};

/**
 * SourceBadge — displays source provenance consistently.
 * 
 * Variants:
 *   badge — compact pill (default, for lists)
 *   tag — small inline tag
 *   full — badge + secondary label (for detail views)
 *   admin — full provenance with raw source (for admin/Elder)
 */
export function SourceBadge({ source, variant = 'badge', className = '' }) {
  const info = resolveSource(source);
  const IconComponent = ICON_MAP[info.icon] || Sparkles;

  if (variant === 'tag') {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-medium ${info.textColor} ${className}`} data-testid="source-tag">
        <IconComponent className="w-2.5 h-2.5" />
        {info.primary}
      </span>
    );
  }

  if (variant === 'full') {
    return (
      <div className={`inline-flex flex-col ${className}`} data-testid="source-full">
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${info.lightBg} ${info.textColor}`}>
          <IconComponent className="w-3 h-3" />
          {info.primary}
        </span>
        <span className="text-[9px] text-[#A8A29E] mt-0.5 pl-1">{info.secondary}</span>
      </div>
    );
  }

  if (variant === 'admin') {
    return (
      <div className={`inline-flex flex-col ${className}`} data-testid="source-admin">
        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${info.color}`}>
          <IconComponent className="w-3 h-3" />
          {info.primary}
        </span>
        <span className="text-[9px] text-[#A8A29E] mt-0.5 pl-1">{info.secondary}</span>
        <span className="text-[8px] text-[#A8A29E] pl-1">raw: {source}</span>
      </div>
    );
  }

  // Default: badge
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${info.lightBg} ${info.textColor} ${className}`}
      data-testid="source-badge" title={info.secondary}>
      <IconComponent className="w-3 h-3" />
      {info.primary}
    </span>
  );
}

/**
 * SourceLine — inline source metadata for cards.
 * Shows: icon + primary label + optional detail.
 */
export function SourceLine({ source, detail, className = '' }) {
  const info = resolveSource(source);
  const IconComponent = ICON_MAP[info.icon] || Sparkles;

  return (
    <span className={`inline-flex items-center gap-1 text-[10px] text-[#A8A29E] ${className}`} data-testid="source-line">
      <IconComponent className="w-2.5 h-2.5" />
      <span>{info.primary}</span>
      {detail && <span className="opacity-70">&middot; {detail}</span>}
    </span>
  );
}
